import {
  LoadingOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Slider, Spin } from "antd";
import { isNil } from "ramda";
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { VIDEOSPEED } from "@/libs/constants";
import { runPrefixMethod } from "@/utils/full-screen";
import { formatClock } from "@/utils/progress-time";

import { Icon } from "../icon";
import styles from "./index.module.less";
import { IConvertedList, ICustomMediaProps, IMediaRef } from "./props";

const CustomMedia = forwardRef(
  (
    {
      convertedList,
      eaTimbreList,
      sourceType,
      allowFastForward = true,
      allowPlaybackSpeed = true,
      allowEATimbre = false,
      allowSeek = true,
      showAudioIcon = true,
      defaultSrc,
      wrapClassName,
      maskClassName,
      coverSrc,
      showDuration = false,
      onForward,
      onBackward,
      onEnded,
      onPlay,
      onError,
      onTimeUpdate,
      onTimbreChange,
    }: ICustomMediaProps,
    ref
  ) => {
    const [isPlaying, setIsPlaying] = useState(false); // 是否播放中

    const [currentTime, setCurrentTime] = useState(0); // 当前播放时间

    const [duration, setDuration] = useState(0); // 视频时长

    const [volume, setVolume] = useState(100); // 音量大小

    const [videoQuality, setVideoQuality] = useState<string>(
      convertedList[0]?.key ?? "480p"
    ); // 视频清晰度

    const [currentQualityIndex, setCurrentQualityIndex] = useState<number>(0); // 清晰度index

    const [languageTimbreId, setLanguageTimbreId] = useState<string>(
      eaTimbreList && eaTimbreList[0]?.id + ""
    ); // 语种

    const [playbackSpeed, setPlaybackSpeed] = useState<string>("1.0"); // 视频倍速

    const [isFullScreen, setIsFullScreen] = useState(false); // 是否全屏

    const [isLoading, setIsLoading] = useState(false);

    const [isQualityChange, setIsQualityChange] = useState(false); // 清晰度是否切换

    const mediaRef = useRef<IMediaRef>(null);

    const qualityButtonRef = useRef<HTMLButtonElement>(null);

    const speedButtonRef = useRef<HTMLButtonElement>(null);

    const EATimbreRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => mediaRef.current);

    const togglePlay = () => {
      setIsPlaying(!isPlaying);
      if (isPlaying) {
        mediaRef.current?.pause();
      } else {
        mediaRef.current?.play();
      }
    };

    const handleTimeUpdate = () => {
      if (isQualityChange && mediaRef.current) {
        // 如果正在切换播放源
        mediaRef.current.currentTime = currentTime;
        mediaRef.current?.play();
        setIsQualityChange(false);
        if (isPlaying) {
          mediaRef.current.play();
        } else {
          mediaRef.current.pause();
        }

        return;
      }

      setCurrentTime(mediaRef.current?.currentTime || 0);
      mediaRef.current && onTimeUpdate?.(mediaRef.current);
    };

    const handleDurationChange = () => {
      setDuration(mediaRef.current?.duration || 0);
    };

    const handleProgressChange = (newTime: number) => {
      if (mediaRef.current) mediaRef.current.currentTime = newTime;

      setCurrentTime(newTime);
    };

    const toggleFullScreen = () => {
      setIsFullScreen(!isFullScreen);
      if (!isFullScreen) {
        runPrefixMethod(
          document.querySelector("#media-screen"),
          "RequestFullScreen"
        );
      } else {
        runPrefixMethod(document, "CancelFullScreen");
      }
    };

    /** 清晰度切换 */
    const onChangeVideoQuality = (quality: string, index: number) => {
      setIsQualityChange(true);
      setVideoQuality(quality);
      setCurrentQualityIndex(index);
    };

    /** 倍速调节 */
    const onChangePlaybackSpeed = (speed: string) => {
      setPlaybackSpeed(speed);
      if (mediaRef.current) mediaRef.current.playbackRate = Number(speed);
    };

    /** 音量调节 */
    const onChangeVolume = (newVolume: number) => {
      setVolume(newVolume);

      if (mediaRef.current) {
        mediaRef.current.volume = newVolume / 100;
        mediaRef.current.muted = false;
      }
    };

    /** 音色调节 */
    const onChangeTimbre = (languageTimbreId: string, index: number) => {
      setLanguageTimbreId(languageTimbreId);
      onTimbreChange && onTimbreChange(index);
    };

    // 监听课件变化，重置当前时间
    useEffect(() => {
      setCurrentTime(0);
      setIsPlaying(false);
      setVideoQuality(convertedList[0]?.key ?? "480p");
      setCurrentQualityIndex(0);
      setPlaybackSpeed("1.0");
    }, [convertedList]);

    useEffect(() => {
      setLanguageTimbreId(eaTimbreList[0]?.id ?? "");
    }, [eaTimbreList]);

    // 监听进度条，修改滑块的初始状态样式以及到底的样式
    useEffect(() => {
      const rangeElements = document.getElementsByClassName("ant-slider");

      if (rangeElements) {
        const rangeThumbElement = rangeElements[0].children[3] as HTMLElement;

        const rangeThumbStyle = rangeThumbElement?.style;

        const screenWidth =
          document.getElementById("media-screen")?.offsetWidth;

        if (rangeThumbStyle && screenWidth) {
          // 视频进度比例
          const scheduleRatio = screenWidth * (currentTime / duration);

          if (scheduleRatio <= 7) rangeThumbStyle.left = "7px";
          else if (currentTime === duration) {
            rangeThumbStyle.left = isFullScreen ? "99.6%" : "99.4%";
          }
        }
      }
    }, [mediaRef, currentTime, duration, isFullScreen]);

    // 根据按下的键盘按键来判断是快进还是后退操作
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // 获取按下的键盘按键
        const key = event.key;

        // 判断按下的键盘按键是快进还是后退
        if (
          key === "ArrowRight" &&
          isFullScreen &&
          mediaRef.current &&
          allowFastForward
        ) {
          // 快进操作
          // 更新视频的当前播放时间
          mediaRef.current.currentTime = currentTime + 5;
          setCurrentTime(currentTime + 5); // 这里每次快进5秒
        } else if (
          key === "ArrowLeft" &&
          isFullScreen &&
          mediaRef.current &&
          allowFastForward
        ) {
          // 后退操作
          // 更新视频的当前播放时间
          mediaRef.current.currentTime = currentTime - 5;
          setCurrentTime(currentTime - 5); // 这里每次后退5秒
        }
      };

      // 添加键盘事件监听器
      window.addEventListener("keydown", handleKeyDown);

      // 在组件卸载时移除键盘事件监听器
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [currentTime, isFullScreen, allowFastForward, mediaRef]);

    // 监听视频缓冲卡顿，显示加载动画
    useEffect(() => {
      const videoElement = mediaRef.current;

      const handleWaiting = () => {
        // 视频开始缓冲，执行相应操作
        // 显示加载动画或者提示用户等待
        setIsLoading(true);
      };

      const handleStalled = () => {
        // 视频缓冲中断，执行相应操作
        // 显示加载动画或者提示用户等待
        setIsLoading(true);
      };

      const handlePlaying = () => {
        setIsLoading(false);
      };

      videoElement?.addEventListener("waiting", handleWaiting);
      videoElement?.addEventListener("stalled", handleStalled);
      videoElement?.addEventListener("playing", handlePlaying);

      return () => {
        videoElement?.removeEventListener("waiting", handleWaiting);
        videoElement?.removeEventListener("stalled", handleStalled);
        videoElement?.removeEventListener("playing", handlePlaying);
      };
    }, [mediaRef]);

    // 添加监听媒体区域鼠标移动, 控制controls显隐
    useEffect(() => {
      let timer: NodeJS.Timeout;

      const mediaElemnt = document.querySelector("#media-screen");

      const controls: HTMLDivElement | null =
        document.querySelector("#media-controls");

      const onDisplayControls = () => {
        clearTimeout(timer);
        if (controls) {
          controls.style.display = "flex";
          timer = setTimeout(() => {
            controls.style.display = "none";
          }, 3000);
        }
      };

      if (mediaElemnt) {
        mediaElemnt.addEventListener("mousemove", onDisplayControls);
      }

      onDisplayControls();

      return () => {
        clearTimeout(timer);
        mediaElemnt?.removeEventListener("mousemove", onDisplayControls);
      };
    }, []);

    // 音量条渲染
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dropdownVolumeRender = (node: React.ReactNode) => {
      return (
        <div className={styles["dropdown-volume"]}>
          <span className={styles["volume"]}>{volume}</span>
          <Slider
            className={styles["volume-slider"]}
            value={volume}
            max={100}
            vertical
            tooltip={{ open: false }}
            onChange={onChangeVolume}
          />
        </div>
      );
    };

    // 下拉菜单渲染
    const dropdownRender = (
      list: { key: string; label: string }[],
      onClick: (key: string, index: number) => void,
      node: HTMLElement | null,
      idName: string
    ) => {
      node && handlePosition(node, idName);

      return (
        <div className={styles["dropdown"]} id={"dropdown-" + idName}>
          <div
            className={styles["dropdown-menu"]}
            id={"dropdown-menu-" + idName}
          >
            {list.map((item, index) => (
              <div
                key={item.key}
                className={`${styles["dropdown-menu-item"]} ${
                  (item.key === videoQuality ||
                    item.key === playbackSpeed ||
                    item.key === languageTimbreId) &&
                  styles["dropdown-menu-item-active"]
                }`}
                onClick={() => onClick(item.key, index)}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
      );
    };

    // 计算菜单位置，top类型
    const handlePosition = (node: HTMLElement, idName: string) => {
      const menu = document.getElementById("dropdown-menu-" + idName);

      const dropdown = document.getElementById("dropdown-" + idName);

      if (menu) {
        node.addEventListener("mouseenter", () => {
          // 当鼠标进入元素时
          menu.style.display = "flex";

          if (dropdown) {
            const x = node.offsetLeft - 0;

            const y = node.offsetTop - 0;

            dropdown.style.left = x - 152 / 2 + 50 + "px";
            dropdown.style.bottom = y + 24 + "px";
          }
        });
        node.addEventListener("mouseleave", () => {
          // 当鼠标离开元素时
          menu.style.display = "none";
        });
      }
    };

    const antLoadingIcon = (
      <LoadingOutlined
        style={{ fontSize: 48, color: "white", zIndex: 200 }}
        spin
      />
    );

    const mediaPlayEnded = () => {
      setIsPlaying(false);
      mediaRef.current?.pause();
      mediaRef.current && onEnded?.(mediaRef.current);
    };

    const mediaPlay = () => {
      mediaRef.current && onPlay?.(mediaRef.current);
      if (mediaRef.current)
        mediaRef.current.playbackRate = Number(playbackSpeed);
    };

    const mediaError = () => {
      mediaRef.current && onError?.(mediaRef.current);
    };

    return (
      <div
        className={`${styles["custom-player"]} ${wrapClassName}`}
        id="media-screen"
      >
        {sourceType === 1 && (isLoading || isQualityChange) && (
          <Spin indicator={antLoadingIcon} />
        )}
        {!isPlaying && (
          <>
            <div className={`${styles["player-mask"]} ${maskClassName}`} />
            <Icon
              icon="playCircle"
              className={styles["mask-playCircle"]}
              width={61}
              height={61}
              onClick={togglePlay}
            />
          </>
        )}
        {sourceType === 2 && !isNil(coverSrc) && (
          <div
            style={{
              position: "absolute",
              zIndex: 1,
              width: "100%",
              height: "100%",
            }}
            onClick={togglePlay}
          >
            <img
              className="rounded-2xl"
              style={{ objectFit: "fill", width: "100%", height: "100%" }}
              src={coverSrc}
              alt=""
            />
          </div>
        )}
        {sourceType === 2 && isPlaying && showAudioIcon && (
          <Icon
            icon="audio"
            className={styles["mask-playCircle"]}
            width={140}
            height={140}
          />
        )}
        {/* 设置视频的宽高比为16:9 */}
        {sourceType === 1 ? (
          <div style={{ aspectRatio: "16/9" }}>
            <video
              ref={mediaRef}
              src={
                convertedList.length > 0
                  ? convertedList[currentQualityIndex]?.src
                  : defaultSrc
              }
              onTimeUpdate={handleTimeUpdate}
              onDurationChange={handleDurationChange}
              onEnded={mediaPlayEnded}
              onClick={togglePlay}
              onPlay={mediaPlay}
              onError={mediaError}
            />
          </div>
        ) : (
          <div className={styles["audio"]} onClick={togglePlay}>
            <audio
              ref={mediaRef}
              src={convertedList.length > 0 ? convertedList[0].src : defaultSrc}
              onTimeUpdate={handleTimeUpdate}
              onDurationChange={handleDurationChange}
              onEnded={mediaPlayEnded}
              onPlay={mediaPlay}
              onError={mediaError}
            />
          </div>
        )}
        <div className={styles["player-bottom"]} id="media-controls">
          <div className={styles.slider}>
            <div className={styles.overlay} />
            <Slider
              value={currentTime}
              max={duration}
              className={`${styles["range"]} ${
                allowSeek ? styles["range-thumb"] : styles["range-disable"]
              }`}
              onChange={(value) => handleProgressChange(value)}
              tooltip={{ open: false }}
            />
          </div>

          <div className={styles["controls"]}>
            <div className={styles["left"]}>
              {onBackward && (
                <span
                  className={styles["icon-box"]}
                  onClick={() => onBackward()}
                >
                  <StepBackwardOutlined />
                </span>
              )}
              <Icon
                icon={isPlaying ? "pauseCircle" : "playCircle"}
                width={32}
                height={32}
                onClick={togglePlay}
              />
              {onForward && (
                <span
                  className={styles["icon-box"]}
                  onClick={() => onForward()}
                >
                  <StepForwardOutlined />
                </span>
              )}
              {showDuration && (
                <span className="text-[#fff] text-base">
                  {formatClock(currentTime)}/{formatClock(duration)}
                </span>
              )}
            </div>
            <div className={styles["right"]}>
              {sourceType === 1 && allowEATimbre && (
                <Button
                  ref={EATimbreRef}
                  className={styles["button"]}
                  onClick={(e) => e.preventDefault()}
                >
                  {
                    eaTimbreList.find((x) => x.id == languageTimbreId)
                      ?.language_text
                  }
                  {/* TODO EA对接时更新 */}
                  <Icon icon="downArrow" width={16} height={16} />
                </Button>
              )}
              {sourceType === 1 && !!convertedList.length && (
                <Button
                  ref={qualityButtonRef}
                  className={styles["button"]}
                  onClick={(e) => e.preventDefault()}
                >
                  {videoQuality}
                  <Icon icon="downArrow" width={16} height={16} />
                </Button>
              )}
              {allowPlaybackSpeed && (
                <Button
                  ref={speedButtonRef}
                  className={styles["button"]}
                  onClick={(e) => e.preventDefault()}
                >
                  {playbackSpeed}X
                  <Icon icon="downArrow" width={16} height={16} />
                </Button>
              )}
              <Dropdown
                menu={{ items: [] }}
                placement="top"
                trigger={["click"]}
                dropdownRender={(node) => dropdownVolumeRender(node)}
                getPopupContainer={(e) => e.parentNode as HTMLElement}
              >
                <Icon
                  icon="speaker"
                  width={24}
                  height={24}
                  onClick={(e) => e.preventDefault()}
                />
              </Dropdown>
              <Icon
                icon="screamFull"
                width={24}
                height={24}
                onClick={toggleFullScreen}
              />
            </div>
          </div>
        </div>
        {allowEATimbre &&
          dropdownRender(
            eaTimbreList.map((val) => {
              return {
                key: val.id,
                label: val.language_text ?? "",
                src: "",
              } as IConvertedList;
            }),
            onChangeTimbre,
            EATimbreRef.current,
            "eaTimbre"
          )}
        {/* 清晰度菜单 */}
        {!!convertedList.length &&
          sourceType === 1 &&
          dropdownRender(
            convertedList,
            onChangeVideoQuality,
            qualityButtonRef.current,
            "quality"
          )}
        {/* 清晰度菜单-end */}
        {/* 倍速菜单-end */}
        {allowPlaybackSpeed &&
          dropdownRender(
            VIDEOSPEED,
            onChangePlaybackSpeed,
            speedButtonRef.current,
            "speed"
          )}
        {/* 倍速菜单-end */}
      </div>
    );
  }
);

export default memo(CustomMedia);
